import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
	Input,
	Button,
	Dropdown,
	DropdownTrigger,
	DropdownMenu,
	DropdownItem
} from '@heroui/react';
import { Pencil } from 'lucide-react';
import useBreakpoint from '../hooks/useBreakpoint.js';

/**
 * SavedFiltersDropdown — fully presentational row that renders the saved
 * filter controls for `CalendarFilterBar` Row 1. No Redux, no API; parent
 * wires handlers.
 *
 * Two modes (epic ENH-SavedFiltersMode):
 * - SELECT (default): `[Saved ▾] [✏️]` — selecting applies the filter; the
 *   pencil enters edit mode.
 * - EDIT: `[Filter name input]` + action buttons — `Save` when no filter is
 *   active (create-new, disabled until the name is non-empty), or `Update` +
 *   `Delete` when a filter is active — plus `Done` to exit. All actions are
 *   real buttons (consistent `flat`/`bordered`), evenly laid out on one row
 *   (epic REM-SavedFiltersButtons). Delete uses `window.confirm`.
 */
function SavedFiltersDropdown({
	savedFilters = [],
	activeId = null,
	loading = false,
	onSelect,
	onSave,
	onUpdate,
	onDelete,
	onClearActive,
	compact = false
}) {
	const activeFilter = useMemo(
		() =>
			activeId != null ? savedFilters.find(f => String(f.id) === String(activeId)) || null : null,
		[savedFilters, activeId]
	);

	const [name, setName] = useState(activeFilter ? activeFilter.name : '');

	// Keep the input in sync when the active filter changes externally
	// (user picks something from the dropdown → parent reselects).
	useEffect(() => {
		if (activeFilter) setName(activeFilter.name || '');
		else setName('');
	}, [activeFilter]);

	const trimmedName = name.trim();
	const canSave = trimmedName.length > 0 && !loading;
	const canUpdate = activeFilter != null && canSave;
	const hasActive = activeFilter != null;

	const handleSave = useCallback(() => {
		if (!canSave || typeof onSave !== 'function') return;
		onSave(trimmedName);
	}, [canSave, onSave, trimmedName]);

	const handleUpdate = useCallback(() => {
		if (!canUpdate || typeof onUpdate !== 'function') return;
		onUpdate(activeFilter.id, trimmedName);
	}, [canUpdate, onUpdate, activeFilter, trimmedName]);

	const handleDelete = useCallback(() => {
		if (!hasActive || typeof onDelete !== 'function') return;
		const confirmMessage = `Delete saved filter "${activeFilter.name}"?`;
		if (typeof window !== 'undefined' && !window.confirm(confirmMessage)) return;
		onDelete(activeFilter.id);
	}, [hasActive, onDelete, activeFilter]);

	const handleSelect = useCallback(
		key => {
			if (key === '__clear__') {
				if (typeof onClearActive === 'function') onClearActive();
				return;
			}
			if (typeof onSelect === 'function') onSelect(key);
		},
		[onSelect, onClearActive]
	);

	const triggerLabel = activeFilter ? activeFilter.name : 'Saved filters';

	// Compact = inside the navCompact Filters Drawer (parent threads
	// `compact={navCompact}`) OR viewport <768 if reused outside a drawer. In a
	// fixed-narrow `Drawer size="sm"` the viewport-based `md:` upgrades would
	// activate at tablet widths even though the drawer body stays narrow → the
	// "Save" button overflowed (horizontal scroll). When compact we (a) make Save
	// icon-only and (b) SUPPRESS the `md:` min-width upgrades so no child carries
	// a hard min-width exceeding the drawer body, with the primary group allowed
	// to wrap as a final safety valve.
	const { belowTablet } = useBreakpoint();
	const isCompact = compact || belowTablet;

	// SELECT mode (default) shows only the dropdown + a pencil; EDIT mode (after
	// the pencil) reveals the name input + Save/Update/Delete. Splitting them
	// stops a plain selection from looking like an unsaved edit (epic
	// ENH-SavedFiltersMode).
	const [editing, setEditing] = useState(false);

	// Reusable controls — composed into a single nowrap row on desktop, or
	// clean STACKED rows in compact (drawer / fluid 300px sidebar) so a long
	// active-filter name can't crush the input or orphan the Save icon
	// (epic REM-SavedFilters).
	const inputEl = (
		<Input
			size="sm"
			variant="flat"
			placeholder="Filter name…"
			value={name}
			onValueChange={setName}
			aria-label="Saved filter name"
			className={
				isCompact
					? 'flex-1 min-w-0'
					: 'flex-1 min-w-0 md:flex-none md:min-w-[140px] md:max-w-[200px]'
			}
			isDisabled={loading}
		/>
	);

	const dropdownEl = (
		<Dropdown placement="bottom-start">
			<DropdownTrigger>
				<Button
					size="sm"
					variant="flat"
					className={
						isCompact
							? 'w-full min-w-0 max-w-full justify-between gap-2'
							: 'min-w-[100px] max-w-[140px] md:min-w-[160px] md:max-w-[220px] justify-between gap-2'
					}
					isDisabled={loading && savedFilters.length === 0}
					endContent={<ChevronDownIcon />}
				>
					<span className="truncate text-sm font-medium">{triggerLabel}</span>
				</Button>
			</DropdownTrigger>
			<DropdownMenu
				aria-label="Saved filters"
				emptyContent={loading ? 'Loading…' : 'No saved filters yet'}
				selectionMode="single"
				selectedKeys={activeId != null ? new Set([String(activeId)]) : new Set()}
				onAction={handleSelect}
			>
				{savedFilters.map(f => (
					<DropdownItem key={String(f.id)}>{f.name || `Filter #${f.id}`}</DropdownItem>
				))}
				{hasActive ? (
					<DropdownItem key="__clear__" className="text-default-500" showDivider>
						Clear selection
					</DropdownItem>
				) : null}
			</DropdownMenu>
		</Dropdown>
	);

	// Edit-mode actions are just TWO buttons (user decision, epic
	// REM-SavedFiltersTwoActions): a context-aware **Save** (creates when no
	// filter is active, overwrites the active one otherwise) and **Delete** (only
	// when a filter is active). Exit edit via the ✏️ toggle — no Done button.
	// Both share the `flat` button affordance + `flex-1 min-w-0` in compact so
	// they sit evenly on one row (styling from epic REM-SavedFiltersButtons).
	const compactBtn = isCompact ? 'flex-1 min-w-0' : undefined;
	const saveBtn = (
		<Button
			size="sm"
			color="primary"
			variant="flat"
			onPress={hasActive ? handleUpdate : handleSave}
			isDisabled={hasActive ? !canUpdate : !canSave}
			aria-label={hasActive ? 'Save changes to filter' : 'Save filter'}
			className={compactBtn}
		>
			Save
		</Button>
	);
	const deleteBtn = (
		<Button
			size="sm"
			color="danger"
			variant="flat"
			onPress={handleDelete}
			isDisabled={loading}
			className={compactBtn}
		>
			Delete
		</Button>
	);

	// Pencil TOGGLES edit mode — enters from SELECT, exits from EDIT (replaces the
	// old Done button). Shows an active (flat/primary) state while editing.
	const pencilBtn = (
		<Button
			isIconOnly
			size="sm"
			variant={editing ? 'flat' : 'light'}
			color={editing ? 'primary' : 'default'}
			aria-label={editing ? 'Stop editing saved filters' : 'Edit saved filters'}
			className="shrink-0"
			onPress={() => setEditing(e => !e)}
		>
			<Pencil className="w-4 h-4" aria-hidden="true" />
		</Button>
	);

	// SELECT mode (default): just the dropdown (select applies immediately) + the
	// pencil to enter edit. The row trivially fits any narrow container.
	if (!editing) {
		return (
			<div className="flex items-center gap-2 min-w-0" data-testid="saved-filters-dropdown">
				<div className="flex-1 min-w-0">{dropdownEl}</div>
				{pencilBtn}
			</div>
		);
	}

	// EDIT mode actions: Save (always) + Delete (only when a filter is active).
	const editActions = (
		<>
			{saveBtn}
			{hasActive && deleteBtn}
		</>
	);

	// Compact (drawer / fluid 300px sidebar): row 1 = input + ✏️ (exit), row 2 =
	// the action buttons evenly on one row.
	if (isCompact) {
		return (
			<div className="flex flex-col gap-2 min-w-0" data-testid="saved-filters-dropdown">
				<div className="flex items-center gap-2 min-w-0">
					{inputEl}
					{pencilBtn}
				</div>
				<div className="flex items-center gap-2 min-w-0">{editActions}</div>
			</div>
		);
	}

	// Desktop inline edit row: input + ✏️ (exit) + Save/Delete.
	return (
		<div className="flex flex-wrap items-center gap-2 min-w-0" data-testid="saved-filters-dropdown">
			<div className="flex items-center gap-2 min-w-0 flex-1 flex-nowrap">
				{inputEl}
				{pencilBtn}
			</div>
			{editActions}
		</div>
	);
}

function ChevronDownIcon() {
	return (
		<svg
			width="14"
			height="14"
			viewBox="0 0 24 24"
			fill="none"
			className="shrink-0 text-default-500"
			aria-hidden="true"
		>
			<path
				d="M6 9l6 6 6-6"
				stroke="currentColor"
				strokeWidth="2"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
		</svg>
	);
}

export default SavedFiltersDropdown;
